import React, { useMemo, useState } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, ProvenanceFooter, ProvenanceBadge, BandPill, Chip, ScoreCell } from '../components/ui/kits.jsx';
import { fmt, bandColor, band as bandOf, CRIT_ORDER, CRIT_COLOR, CRIT_ABBR } from '../lib/format.js';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { Sliders, RotateCcw, TrendingUp, TrendingDown, Target, Zap } from 'lucide-react';

/**
 * Simulator — Programme What-If sandbox.
 *
 * Adjust the 7 OECD criterion weights → live-recompute every project's
 * weighted overall + band, then show what moves in the rankings.
 * All operations are pure and side-effect-free.
 */
const SCENARIOS = {
  official: { name: 'Official Weights', weights: null, hint: 'HDFC Parivartan framework' },
  sustainability: {
    name: 'Sustainability-first',
    weights: { Relevance: 10, Coherence: 5, Efficiency: 10, Effectiveness: 15, Impact: 30, Sustainability: 25, Branding: 5 },
    hint: 'Lasting change over short-term reach',
  },
  delivery: {
    name: 'Speed of Delivery',
    weights: { Relevance: 10, Coherence: 5, Efficiency: 40, Effectiveness: 30, Impact: 10, Sustainability: 5, Branding: 0 },
    hint: 'Execution & throughput lens',
  },
  equity: {
    name: 'Equity Lens',
    weights: { Relevance: 20, Coherence: 10, Efficiency: 10, Effectiveness: 10, Impact: 30, Sustainability: 20, Branding: 0 },
    hint: 'Reach the hardest-to-serve',
  },
  balanced: {
    // Integer weights summing exactly to 100 (100 / 7 = 14.28 → 14×5 + 15×2 = 100).
    name: 'Balanced (equal)',
    weights: { Relevance: 14, Coherence: 14, Efficiency: 14, Effectiveness: 15, Impact: 15, Sustainability: 14, Branding: 14 },
    hint: 'Every criterion weighted equally',
  },
};

export default function Simulator() {
  const { data, agg: A } = useData();
  const officialWeights = data?.meta?.weights || {};

  // slider state — kept in percentage-of-100
  const [weightsPct, setWeightsPct] = useState(() =>
    Object.fromEntries(CRIT_ORDER.map((c) => [c, Math.round((officialWeights[c] || 0) * 100)]))
  );
  const [activePreset, setActivePreset] = useState('official');

  const sim = useMemo(() => {
    if (!data || !A) return null;
    return simulate(A.projectsSorted, weightsPct, A);
  }, [data, A, weightsPct]);

  if (!data || !A || !sim) return null;

  const sumPct = Object.values(weightsPct).reduce((a, b) => a + b, 0);
  const sumRounded = Math.round(sumPct);
  const off = sumRounded !== 100;

  const applyPreset = (id) => {
    const s = SCENARIOS[id];
    if (id === 'official') {
      setWeightsPct(Object.fromEntries(CRIT_ORDER.map((c) => [c, Math.round((officialWeights[c] || 0) * 100)])));
    } else {
      setWeightsPct(Object.fromEntries(CRIT_ORDER.map((c) => [c, Math.round(s.weights[c] || 0)])));
    }
    setActivePreset(id);
  };

  const setWeight = (c, val) => {
    setWeightsPct((prev) => ({ ...prev, [c]: Math.round(val) }));
    setActivePreset('custom');
  };

  const delta = sim.programmeOverall - A.po;
  const deltaBand = sim.programmeBand !== A.poBand;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-simulator">
      {/* Hero */}
      <div className="card" style={{
        padding: 18, background: 'linear-gradient(120deg, #042a54, #004c8f)',
        color: '#fff', borderColor: '#042a54',
      }} data-testid="simulator-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Sliders size={16} />
          <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', opacity: .8 }}>
            Programme Simulator · What-If Sandbox
          </div>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>
          Change the weights. Watch every project, partner and region re-rank live.
        </div>
        <div style={{ marginTop: 6, fontSize: 11.5, opacity: .75 }}>
          Uses the {A.n} scored projects and their per-criterion sub-scores. No data is written back — this is a pure re-computation sandbox for board planning.
        </div>
      </div>

      {/* Scenario chips */}
      <div className="card" style={{ padding: 16 }} data-testid="scenario-chips">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            Preset Scenarios
          </div>
          <button
            onClick={() => applyPreset('official')}
            data-testid="btn-reset"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'transparent', border: '1px solid var(--line)',
              padding: '5px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 600,
              cursor: 'pointer', color: 'var(--ink-3)',
            }}
          >
            <RotateCcw size={12} /> Reset to Official
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(SCENARIOS).map(([id, s]) => (
            <button
              key={id}
              onClick={() => applyPreset(id)}
              data-testid={`preset-${id}`}
              style={{
                padding: '8px 14px', borderRadius: 999,
                border: `1px solid ${activePreset === id ? 'var(--primary)' : 'var(--line)'}`,
                background: activePreset === id ? 'var(--primary-soft)' : 'var(--surface)',
                color: activePreset === id ? 'var(--primary)' : 'var(--ink-2)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                transition: 'all .15s',
              }}
            >
              {s.name}
              <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--ink-4)', fontWeight: 500 }}>{s.hint}</span>
            </button>
          ))}
          {activePreset === 'custom' && (
            <span style={{
              padding: '8px 14px', borderRadius: 999,
              border: '1px solid #e23d28', background: '#e23d2811',
              color: '#c8452f', fontSize: 12, fontWeight: 700,
            }}>Custom</span>
          )}
        </div>
      </div>

      {/* Sliders + Live Result KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }} className="sim-main-grid">
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={
            <span
              style={{
                fontSize: 11, fontWeight: 700,
                color: off ? '#c8452f' : '#1f8a4c',
              }}
              className="tabular"
              data-testid="weight-sum"
            >
              Σ = {sumRounded}%
            </span>
          }>
            Adjust OECD Criterion Weights
          </SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {CRIT_ORDER.map((c) => {
              const val = Math.round(weightsPct[c] || 0);
              const officialPct = Math.round((officialWeights[c] || 0) * 100);
              const isDefault = val === officialPct;
              return (
                <div key={c} data-testid={`slider-row-${c}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ width: 10, height: 10, background: CRIT_COLOR[c], borderRadius: 3 }} />
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>{c}</span>
                      {!isDefault && (
                        <span style={{ fontSize: 10, color: 'var(--ink-4)', fontStyle: 'italic' }} className="tabular">
                          official {officialPct}%
                        </span>
                      )}
                    </div>
                    <span className="tabular" style={{
                      fontSize: 13, fontWeight: 800,
                      color: isDefault ? 'var(--ink-3)' : CRIT_COLOR[c],
                    }}>{val}%</span>
                  </div>
                  <input
                    type="range" min="0" max="50" step="1" value={val}
                    onChange={(e) => setWeight(c, +e.target.value)}
                    data-testid={`slider-${c}`}
                    style={{
                      width: '100%',
                      accentColor: CRIT_COLOR[c],
                      cursor: 'pointer',
                    }}
                  />
                </div>
              );
            })}
          </div>
          {off && (
            <div style={{
              marginTop: 12, padding: '8px 12px', background: '#fbe7e3',
              border: '1px solid #e23d2833', borderRadius: 6,
              fontSize: 11, color: '#c8452f', fontWeight: 600,
            }} data-testid="sum-warn">
              Weights sum to {sumRounded}% — scores are auto-normalised to 100% for the recomputation.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="kpi-card fade-up" data-testid="sim-programme-overall" style={{
            background: 'linear-gradient(140deg, ' + bandColor(sim.programmeBand) + '22, transparent)',
            borderLeft: '4px solid ' + bandColor(sim.programmeBand),
          }}>
            <div className="kpi-label">Programme Overall · simulated</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 4 }}>
              <span className="tabular" style={{
                fontSize: 40, fontWeight: 800, lineHeight: 1, color: bandColor(sim.programmeBand),
              }}>{fmt(sim.programmeOverall, 2)}</span>
              <BandPill band={sim.programmeBand} />
            </div>
            <div className="kpi-sub" style={{ marginTop: 6 }} data-testid="sim-delta">
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                color: delta > 0 ? '#1f8a4c' : delta < 0 ? '#c8452f' : 'var(--ink-4)',
                fontWeight: 700,
              }} className="tabular">
                {delta > 0 ? <TrendingUp size={12} /> : delta < 0 ? <TrendingDown size={12} /> : null}
                {delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${fmt(delta, 3)} vs official (${fmt(A.po, 2)})`}
              </span>
              {deltaBand && (
                <span style={{ marginLeft: 8, color: '#c8452f', fontWeight: 700 }}>
                  · band shift {A.poBand} → {sim.programmeBand}
                </span>
              )}
            </div>
          </div>

          <div className="card" style={{ padding: 14 }} data-testid="sim-band-mix">
            <div className="kpi-label" style={{ marginBottom: 6 }}>Band Distribution · simulated cohort</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => {
                const count = sim.bandCounts[b] || 0;
                const pct = (count / A.n) * 100;
                const delta = count - (A.projectsSorted.filter((p) => p.band === b).length);
                return (
                  <div key={b} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 44px 42px', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
                    <BandPill band={b} />
                    <div style={{ position: 'relative', height: 8, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pct + '%', background: bandColor(b), transition: 'width .25s' }} />
                    </div>
                    <span className="tabular" style={{ textAlign: 'right', fontWeight: 700 }}>{count}</span>
                    <span className="tabular" style={{ textAlign: 'right', fontSize: 10.5, color: delta > 0 ? '#1f8a4c' : delta < 0 ? '#c8452f' : 'var(--ink-4)', fontWeight: 700 }}>
                      {delta > 0 ? `+${delta}` : delta === 0 ? '±0' : delta}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Movers list */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><TrendingUp size={14} color="#1f8a4c" /><Chip color="#1f8a4c">Movers up</Chip></div>}>
            Projects that gain the most rank
          </SectionTitle>
          <MoverTable rows={sim.moversUp} direction="up" />
        </div>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><TrendingDown size={14} color="#c8452f" /><Chip color="#c8452f">Movers down</Chip></div>}>
            Projects that drop the most rank
          </SectionTitle>
          <MoverTable rows={sim.moversDown} direction="down" />
        </div>
      </div>

      {/* Region winners */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="Recomputed under simulated weights" />}>
          Regional Winners & Losers under this scenario
        </SectionTitle>
        <table className="pv-table">
          <thead>
            <tr>
              <th>Region</th>
              <th style={{ textAlign: 'right' }}>Projects</th>
              <th style={{ textAlign: 'right' }}>Official avg</th>
              <th style={{ textAlign: 'right' }}>Simulated avg</th>
              <th style={{ textAlign: 'right' }}>Δ</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {sim.regions.map((r) => (
              <tr key={r.region} data-testid={`region-sim-${r.region}`}>
                <td style={{ fontWeight: 700 }}>{r.region}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{r.n}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{fmt(r.official, 2)}</td>
                <td className="tabular" style={{ textAlign: 'right', fontWeight: 700, color: bandColor(bandOf(r.simulated)) }}>{fmt(r.simulated, 2)}</td>
                <td className="tabular" style={{ textAlign: 'right', fontWeight: 700, color: r.delta > 0 ? '#1f8a4c' : r.delta < 0 ? '#c8452f' : 'var(--ink-4)' }}>
                  {r.delta > 0 ? '+' : ''}{fmt(r.delta, 3)}
                </td>
                <td>
                  {r.delta > 0.05 ? <Chip color="#1f8a4c">Wins</Chip>
                    : r.delta < -0.05 ? <Chip color="#c8452f">Loses</Chip>
                      : <Chip>Neutral</Chip>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProvenanceFooter text={`Simulation applies user-chosen weights to each project's stored per-criterion sub-scores (${CRIT_ORDER.join(' · ')}). No live queries or writebacks — this is a pure client-side re-projection over the same 186 evaluated projects. Weights are auto-normalised to 100% if the sliders sum to a different value.`} />
    </div>
  );
}

function MoverTable({ rows, direction }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 14, color: 'var(--ink-4)', fontStyle: 'italic', fontSize: 12 }}>No significant movers under this scenario.</div>;
  }
  return (
    <table className="pv-table">
      <thead>
        <tr>
          <th style={{ width: 40 }}>Rank</th>
          <th>Project</th>
          <th style={{ textAlign: 'right' }}>Official</th>
          <th style={{ textAlign: 'right' }}>Simulated</th>
          <th style={{ textAlign: 'right' }}>Δ rank</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 8).map((r) => (
          <tr key={r.code}>
            <td className="tabular" style={{ color: 'var(--ink-4)', fontWeight: 700 }}>#{r.newRank}</td>
            <td>
              <div style={{ fontWeight: 700, fontSize: 12.5 }}><ProjectCode code={r.code} /></div>
              <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{r.partner || '—'} · {r.type}</div>
            </td>
            <td className="tabular" style={{ textAlign: 'right' }}>{fmt(r.official, 2)}</td>
            <td className="tabular" style={{ textAlign: 'right', fontWeight: 700, color: bandColor(bandOf(r.simulated)) }}>{fmt(r.simulated, 2)}</td>
            <td className="tabular" style={{ textAlign: 'right', fontWeight: 800, color: direction === 'up' ? '#1f8a4c' : '#c8452f' }}>
              {direction === 'up' ? '↑' : '↓'} {Math.abs(r.rankDelta)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ------------------------------------------------------------------- Simulator core

/**
 * simulate(projects, weightsPct, A) — pure recomputation.
 * Weights auto-normalise to sum 1.0.
 */
function simulate(projects, weightsPct, A) {
  const sum = Object.values(weightsPct).reduce((a, b) => a + b, 0);
  const denom = sum > 0 ? sum : 100;
  const w = Object.fromEntries(CRIT_ORDER.map((c) => [c, (weightsPct[c] || 0) / denom]));

  // Per-project recompute
  const recomputed = projects.map((p, i) => {
    let total = 0;
    let wSum = 0;
    CRIT_ORDER.forEach((c) => {
      const v = p.criteria?.[c];
      if (v != null && !Number.isNaN(v)) {
        total += v * w[c];
        wSum += w[c];
      }
    });
    const simScore = wSum > 0 ? total / wSum : null;
    return {
      ...p,
      officialRank: i + 1,
      officialOverall: p.overall,
      simOverall: simScore,
      simBand: simScore != null ? bandOf(simScore) : null,
    };
  });

  const bySim = [...recomputed]
    .filter((r) => r.simOverall != null)
    .sort((a, b) => b.simOverall - a.simOverall);
  bySim.forEach((r, i) => { r.simRank = i + 1; });

  const bandCounts = { Excellent: 0, Good: 0, Moderate: 0, Poor: 0 };
  bySim.forEach((r) => { if (r.simBand) bandCounts[r.simBand] = (bandCounts[r.simBand] || 0) + 1; });

  const programmeOverall = bySim.reduce((a, r) => a + r.simOverall, 0) / bySim.length;
  const programmeBand = bandOf(programmeOverall);

  const movers = bySim
    .map((r) => ({
      code: r.code, partner: r.partner, type: r.type,
      official: r.officialOverall, simulated: r.simOverall,
      newRank: r.simRank, oldRank: r.officialRank,
      rankDelta: r.officialRank - r.simRank, // positive = moved up
    }))
    .filter((r) => Math.abs(r.rankDelta) > 0);
  const moversUp = [...movers].sort((a, b) => b.rankDelta - a.rankDelta).filter((r) => r.rankDelta > 0);
  const moversDown = [...movers].sort((a, b) => a.rankDelta - b.rankDelta).filter((r) => r.rankDelta < 0);

  // Region deltas
  const byRegion = {};
  recomputed.forEach((r) => {
    if (!r.region) return;
    if (!byRegion[r.region]) byRegion[r.region] = { region: r.region, official: [], simulated: [] };
    if (r.officialOverall != null) byRegion[r.region].official.push(r.officialOverall);
    if (r.simOverall != null) byRegion[r.region].simulated.push(r.simOverall);
  });
  const regions = Object.values(byRegion).map((r) => {
    const off = r.official.length ? r.official.reduce((a, b) => a + b, 0) / r.official.length : 0;
    const sim = r.simulated.length ? r.simulated.reduce((a, b) => a + b, 0) / r.simulated.length : 0;
    return {
      region: r.region, n: r.simulated.length,
      official: off, simulated: sim, delta: sim - off,
    };
  }).sort((a, b) => b.delta - a.delta);

  return { programmeOverall, programmeBand, bandCounts, moversUp, moversDown, regions };
}
